import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faComments,
  faPaperPlane,
  faTicket,
  faSignal,
  faCircleExclamation,
  faCopy,
  faCheck,
  faArrowRight,
  faGaugeHigh,
  faAddressCard,
  faWandMagicSparkles,
  faGlobe
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { API_BASE } from 'apiConfig';
import Avatar from 'components/base/Avatar';

interface Totals {
  visitors: number;
  page_views: number;
  chats_started: number;
  chats_closed: number;
  messages_customer: number;
  messages_agent: number;
  tickets_created: number;
}

interface Membership {
  plan: string;
  status: string;
  active: boolean;
  interval: string | null;
  current_period_end: string | null;
}

const ymd = (d: Date) => d.toISOString().slice(0, 10);

const planName = (key?: string) =>
  key ? key.charAt(0).toUpperCase() + key.slice(1) : 'Free';

// A tiny dependency-free area sparkline themed via Bootstrap CSS vars.
const Sparkline = ({ values }: { values: number[] }) => {
  const w = 100;
  const h = 36;
  if (!values.length) return null;
  const max = Math.max(1, ...values);
  const step = values.length > 1 ? w / (values.length - 1) : w;
  const pts = values.map((v, i) => [i * step, h - (v / max) * (h - 4) - 2]);
  const line = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `0,${h} ${line} ${w},${h}`;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height: 40, display: 'block' }}
    >
      <polygon points={area} fill="var(--phoenix-primary)" opacity={0.12} />
      <polyline
        points={line}
        fill="none"
        stroke="var(--phoenix-primary)"
        strokeWidth={1.5}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

const MainDashboard = () => {
  const navigate = useNavigate();
  const token = useSelector((s: RootState) => s.auth.token);
  const firstName = (localStorage.getItem('name') || '').trim();
  const avatarUrl = (localStorage.getItem('avatar_url') || '').trim();
  const initials = firstName
    ? firstName
        .split(' ')
        .map(p => p[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '';
  const license = useSelector(
    (s: RootState) => s.auth.widget_license || s.auth.license_uuid
  );
  const customers = useSelector((s: RootState) => s.traffic.customers);

  const [totals, setTotals] = useState<Totals | null>(null);
  const [series, setSeries] = useState<any[]>([]);
  const [topPages, setTopPages] = useState<any[]>([]);
  const [membership, setMembership] = useState<Membership | null>(null);
  const [everInstalled, setEverInstalled] = useState<boolean | null>(null);
  const [copied, setCopied] = useState(false);

  const onlineNow = useMemo(
    () => (customers || []).filter((c: any) => c.active).length,
    [customers]
  );

  const codeSnippet = `<!-- Begin EasyChatDesk widget -->
<script>
  window.__ecd = { license: '${license}' };
</script>
<script src="https://cdn.easychatdesk.com/widget.min.js" async></script>
<!-- End EasyChatDesk widget -->`;

  const copySnippet = () => {
    navigator.clipboard.writeText(codeSnippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const load = useCallback(async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    const from14 = ymd(new Date(Date.now() - 13 * 86400000));
    const get = (url: string) =>
      fetch(`${API_BASE}${url}`, { headers })
        .then(r => (r.ok ? r.json() : null))
        .catch(() => null);

    const [overview, allTime, ts, pages, member] = await Promise.all([
      get('/analytics/overview'),
      get('/analytics/overview?from=2000-01-01'),
      get(`/analytics/timeseries?from=${from14}`),
      get('/analytics/top-pages'),
      get('/billing/membership')
    ]);

    if (overview?.totals) setTotals(overview.totals);
    if (ts?.series) setSeries(ts.series);
    if (pages?.pages) setTopPages(pages.pages.slice(0, 6));
    if (member) setMembership(member);

    const ptViews = Number(allTime?.totals?.page_views || 0);
    const ptVisitors = Number(allTime?.totals?.visitors || 0);
    setEverInstalled(ptViews > 0 || ptVisitors > 0);
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // Live traffic right now is the strongest "installed" signal of all.
  const installed = everInstalled || onlineNow > 0 || (customers || []).length > 0;
  const showInstallNotice = everInstalled !== null && !installed;

  const messages =
    (totals?.messages_customer || 0) + (totals?.messages_agent || 0);

  const statCards = [
    { label: 'Online now', value: onlineNow, icon: faSignal, color: 'success' },
    { label: 'Visitors (30d)', value: totals?.visitors ?? '—', icon: faUsers, color: 'primary' },
    { label: 'Conversations (30d)', value: totals?.chats_started ?? '—', icon: faComments, color: 'info' },
    { label: 'Messages (30d)', value: totals != null ? messages : '—', icon: faPaperPlane, color: 'primary' },
    { label: 'Tickets (30d)', value: totals?.tickets_created ?? '—', icon: faTicket, color: 'warning' }
  ];

  const sparkValues = series.map((d: any) => Number(d.visitors || 0));
  const visitors14 = sparkValues.reduce((a, b) => a + b, 0);

  const quickActions = [
    { label: 'Install widget', icon: faGaugeHigh, to: '/settings/install' },
    { label: 'Saved replies', icon: faWandMagicSparkles, to: '/settings/saved-replies' },
    { label: 'Contact forms', icon: faAddressCard, to: '/contact-forms' },
    { label: 'Live traffic', icon: faGlobe, to: '/traffic' }
  ];

  return (
    <div className="pb-6">
      <div className="mb-5 d-flex align-items-center gap-3">
        {avatarUrl ? (
          <Avatar src={avatarUrl} size="xl" />
        ) : (
          <Avatar size="xl" variant="name">
            {initials || '?'}
          </Avatar>
        )}
        <div>
          <h2 className="mb-1 text-body-emphasis">
            {firstName ? `Welcome back, ${firstName.split(' ')[0]}` : 'My Dashboard'}
          </h2>
          <h5 className="text-body-tertiary fw-semibold mb-0">
            Here’s what’s going on at your business right now
          </h5>
        </div>
      </div>

      {showInstallNotice && (
        <Card className="mb-4 border-warning-subtle bg-warning-subtle">
          <Card.Body>
            <div className="d-flex align-items-start gap-3">
              <FontAwesomeIcon
                icon={faCircleExclamation}
                className="text-warning fs-5 mt-1"
              />
              <div className="flex-1">
                <h4 className="mb-1 text-body-emphasis">
                  Your chat widget isn’t installed yet
                </h4>
                <p className="text-body-secondary mb-3">
                  We haven’t seen any visitors from your website. Add this snippet
                  just before the closing <code>{'</body>'}</code> tag on every page
                  to start receiving live chats and tracking visitors.
                </p>
                <pre
                  className="bg-body-emphasis text-body-emphasis rounded p-3 mb-3"
                  style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}
                >
                  {codeSnippet}
                </pre>
                <div className="d-flex flex-wrap gap-2">
                  <Button variant="primary" size="sm" onClick={copySnippet}>
                    <FontAwesomeIcon
                      icon={copied ? faCheck : faCopy}
                      className="me-2"
                    />
                    {copied ? 'Copied!' : 'Copy snippet'}
                  </Button>
                  <Button
                    variant="phoenix-secondary"
                    size="sm"
                    onClick={() => navigate('/settings/install')}
                  >
                    Open install guide
                    <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </Button>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      <Row className="g-3 mb-4">
        {statCards.map(s => (
          <Col xs={6} md={4} xl key={s.label}>
            <Card className="h-100">
              <Card.Body className="py-3">
                <div className="d-flex align-items-center mb-2">
                  <FontAwesomeIcon
                    icon={s.icon}
                    className={`text-${s.color} me-2`}
                  />
                  <span className="fs-9 text-body-tertiary">{s.label}</span>
                </div>
                <div className="fs-3 fw-bolder text-body-emphasis lh-1">
                  {s.value}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Row className="g-3">
        <Col xs={12} lg={8}>
          <Card className="h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <h5 className="mb-0 text-body-emphasis">Visitors</h5>
                  <span className="fs-9 text-body-tertiary">Last 14 days</span>
                </div>
                <div className="text-end">
                  <div className="fs-4 fw-bolder text-body-emphasis lh-1">
                    {visitors14}
                  </div>
                  <span className="fs-9 text-body-tertiary">total</span>
                </div>
              </div>
              {sparkValues.length ? (
                <Sparkline values={sparkValues} />
              ) : (
                <p className="text-body-tertiary fs-9 mb-0 py-4 text-center">
                  No visitor data yet.
                </p>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={4}>
          <Card className="h-100">
            <Card.Body>
              <h5 className="mb-3 text-body-emphasis">Your plan</h5>
              {membership ? (
                <>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span className="fs-4 fw-bolder text-body-emphasis">
                      {planName(membership.plan)}
                    </span>
                    <Badge bg={membership.active ? 'success' : 'secondary'}>
                      {membership.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <p className="fs-9 text-body-tertiary mb-3">
                    {membership.active
                      ? membership.current_period_end
                        ? `Renews ${new Date(
                            membership.current_period_end
                          ).toLocaleDateString()}`
                        : 'Your subscription is active.'
                      : 'Upgrade to unlock more features.'}
                  </p>
                </>
              ) : (
                <p className="fs-9 text-body-tertiary mb-3">Loading…</p>
              )}
              <Button
                variant="phoenix-primary"
                size="sm"
                className="w-100"
                onClick={() => navigate('/billing')}
              >
                Manage plan
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={8}>
          <Card className="h-100">
            <Card.Body>
              <h5 className="mb-3 text-body-emphasis">Top pages (30 days)</h5>
              {topPages.length ? (
                <div className="d-flex flex-column gap-2">
                  {topPages.map((p: any, i: number) => (
                    <div
                      key={p.url || i}
                      className="d-flex align-items-center justify-content-between border-bottom border-translucent pb-2"
                    >
                      <div className="text-truncate me-3">
                        <div className="text-body-emphasis text-truncate fs-9 fw-semibold">
                          {p.title || p.url}
                        </div>
                        <div className="text-body-tertiary text-truncate fs-10">
                          {p.url}
                        </div>
                      </div>
                      <div className="text-end flex-shrink-0">
                        <span className="fw-bold text-body-emphasis">
                          {p.views}
                        </span>
                        <span className="fs-10 text-body-tertiary ms-1">views</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-body-tertiary fs-9 mb-0 py-4 text-center">
                  No page views recorded yet.
                </p>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={4}>
          <Card className="h-100">
            <Card.Body>
              <h5 className="mb-3 text-body-emphasis">Quick actions</h5>
              <div className="d-flex flex-column gap-2">
                {quickActions.map(a => (
                  <Button
                    key={a.to}
                    variant="phoenix-secondary"
                    className="d-flex align-items-center justify-content-between"
                    onClick={() => navigate(a.to)}
                  >
                    <span>
                      <FontAwesomeIcon icon={a.icon} className="me-2 text-primary" />
                      {a.label}
                    </span>
                    <FontAwesomeIcon icon={faArrowRight} className="fs-10" />
                  </Button>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MainDashboard;
